// src/shared/ui/SymbolAutocomplete.tsx
//
// Reusable ticker/symbol typeahead for the PUBLIC free portfolio tools
// (Portfolio Diversification Checker + Portfolio Drift Auditor). It is a
// controlled <input> wrapped in a command-palette-style dropdown:
//
//   * 300ms debounce (useDebounce) so the sub-millisecond /symbols endpoint
//     is hit once per pause, not per keystroke.
//   * Suggestions are drawn from the SAME air-gapped price universe both tools
//     validate against, so every row shown is guaranteed submittable.
//   * Full keyboard control: ArrowUp / ArrowDown to move, Enter to pick the
//     highlighted (or #1) hit, Escape to dismiss. Click-outside closes.
//   * Symbol + company name + colour-coded asset-class badge per row.
//
// AUTH: these tools are unauthenticated marketing pages, so (exactly like the
// feature api hooks) we reuse apiClient's resolved baseURL via a clean,
// interceptor-free axios instance — no Bearer token, no 401 bounce.

import axios from 'axios';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { apiClient } from '../../api/client';
import { useDebounce } from '../hooks/useDebounce';

// The availability universe is shared by both free tools (same air-gapped
// price store), so both call this single public endpoint.
const SUGGEST_PATH = '/portfolio-diversification-checker/symbols';

const publicApiClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 8_000,
    headers: { 'Content-Type': 'application/json' },
});

export interface SymbolSuggestion {
    symbol: string;
    name?: string | null;
    asset_class?: string | null;
    exchange?: string | null;
}

interface SymbolSuggestResponse {
    query: string;
    results: SymbolSuggestion[];
}

/**
 * Query the public symbol typeahead. Fails SILENTLY (returns `[]`) on any
 * error so the autocomplete degrades to "no suggestions" and never blocks the
 * user from typing a ticker by hand.
 */
async function fetchSymbolSuggestions(
    query: string,
    limit = 8,
): Promise<SymbolSuggestion[]> {
    const q = query.trim();
    if (q.length === 0) return [];
    try {
        const { data } = await publicApiClient.get<SymbolSuggestResponse>(
            SUGGEST_PATH,
            { params: { q, limit } },
        );
        return data.results ?? [];
    } catch {
        return [];
    }
}

type Accent = 'sky' | 'indigo';

const ACCENT_ACTIVE_ROW: Record<Accent, string> = {
    sky: 'bg-sky-50',
    indigo: 'bg-indigo-50',
};

const ACCENT_SPINNER: Record<Accent, string> = {
    sky: 'border-t-sky-500',
    indigo: 'border-t-indigo-500',
};

const BADGE_STYLE_BY_CLASS: Record<string, string> = {
    equity: 'bg-blue-100 text-blue-700 border-blue-200',
    etf: 'bg-purple-100 text-purple-700 border-purple-200',
    reit: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    crypto: 'bg-orange-100 text-orange-700 border-orange-200',
};

const badgeStyle = (assetClass?: string | null): string =>
    (assetClass && BADGE_STYLE_BY_CLASS[assetClass]) ??
    'bg-slate-100 text-slate-600 border-slate-200';

export interface SymbolAutocompleteProps {
    value: string;
    onChange: (next: string) => void;
    accent?: Accent;
    placeholder?: string;
    ariaLabel?: string;
    maxLength?: number;
    /** Base input styling supplied by the host form (shared inputClass). */
    inputClassName?: string;
    /** Unique id per row so the listbox a11y wiring stays unambiguous. */
    listboxId: string;
}

export const SymbolAutocomplete = ({
    value,
    onChange,
    accent = 'sky',
    placeholder,
    ariaLabel,
    maxLength = 20,
    inputClassName = '',
    listboxId,
}: SymbolAutocompleteProps) => {
    const [results, setResults] = useState<SymbolSuggestion[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
    // The value we last injected via a suggestion pick. We suppress the
    // immediate re-fetch (and the dropdown re-opening) for that exact string
    // so selecting a row doesn't bounce the menu straight back open.
    const justPickedRef = useRef<string | null>(null);

    const debouncedValue = useDebounce(value, 300);

    useEffect(() => {
        let cancelled = false;
        const q = debouncedValue.trim();

        if (q.length === 0) {
            setResults([]);
            setIsOpen(false);
            return;
        }
        if (justPickedRef.current !== null && justPickedRef.current === debouncedValue) {
            // This change came from a suggestion pick — don't reopen.
            return;
        }

        setIsSearching(true);
        fetchSymbolSuggestions(q)
            .then((data) => {
                if (cancelled) return;
                setResults(data);
                setActiveIndex(-1);
                if (data.length > 0) setIsOpen(true);
            })
            .finally(() => {
                if (!cancelled) setIsSearching(false);
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedValue]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (activeIndex >= 0) {
            itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    const handleChange = (raw: string) => {
        justPickedRef.current = null;
        onChange(raw.toUpperCase());
    };

    const handleSelect = (suggestion: SymbolSuggestion) => {
        justPickedRef.current = suggestion.symbol;
        onChange(suggestion.symbol.toUpperCase());
        setIsOpen(false);
        setResults([]);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            return;
        }
        if (!isOpen || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < results.length) {
                e.preventDefault();
                handleSelect(results[activeIndex]);
            }
            // Enter with no active row falls through so the form can submit.
        }
    };

    const showDropdown = useMemo(
        () => isOpen && (results.length > 0 || isSearching),
        [isOpen, results.length, isSearching],
    );

    return (
        <div
            ref={containerRef}
            className={`relative flex-1 min-w-0 ${isOpen ? 'z-30' : ''}`}
        >
            <input
                type="text"
                role="combobox"
                aria-expanded={showDropdown}
                aria-controls={listboxId}
                aria-autocomplete="list"
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (results.length > 0) setIsOpen(true);
                }}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={maxLength}
                className={`${inputClassName} w-full`}
            />

            {isSearching && (
                <div
                    className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-slate-200 ${ACCENT_SPINNER[accent]} animate-spin`}
                    aria-hidden="true"
                />
            )}

            {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-30">
                    {results.length === 0 && !isSearching ? (
                        <div className="px-4 py-4 text-center text-slate-400 text-sm">
                            No matching symbols.
                        </div>
                    ) : (
                        <ul
                            id={listboxId}
                            role="listbox"
                            className="max-h-72 overflow-y-auto py-1.5 divide-y divide-slate-100"
                        >
                            {results.map((item, idx) => (
                                <li
                                    key={`${item.symbol}-${idx}`}
                                    ref={(el) => {
                                        itemRefs.current[idx] = el;
                                    }}
                                    role="option"
                                    aria-selected={activeIndex === idx}
                                    // onMouseDown (not onClick) fires before the
                                    // input's blur, so the pick isn't lost to the
                                    // click-outside handler.
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(item);
                                    }}
                                    onMouseEnter={() => setActiveIndex(idx)}
                                    className={`px-4 py-2.5 cursor-pointer flex items-center justify-between gap-3 transition-colors ${
                                        activeIndex === idx
                                            ? ACCENT_ACTIVE_ROW[accent]
                                            : 'hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="flex flex-col overflow-hidden">
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-bold text-slate-900 text-sm tracking-tight">
                                                {item.symbol}
                                            </span>
                                            {item.exchange &&
                                                item.asset_class !== 'crypto' && (
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                                                        {item.exchange}
                                                    </span>
                                                )}
                                        </div>
                                        {item.name && (
                                            <span className="text-xs text-slate-500 truncate">
                                                {item.name}
                                            </span>
                                        )}
                                    </div>
                                    {item.asset_class && (
                                        <span
                                            className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${badgeStyle(
                                                item.asset_class,
                                            )}`}
                                        >
                                            {item.asset_class}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default SymbolAutocomplete;
